<template>
  <!-- One card for the whole consumption picture: completeness, totals, locations. -->
  <SectionCard :title="`${domain.name} consumption data`">
    <template #actions>
      <button
        type="button"
        class="btn btn-sm btn-primary text-nowrap"
        @click="$emit('report')"
      >
        <font-awesome-icon icon="paperclip" class="me-1" />Report consumption
      </button>
    </template>

    <div class="cg-kpis">
      <StatBlock
        :value="view.pctActual"
        unit="actual"
        :caption="`Of ${view.totalCells} reporting periods, 2026`"
        size="lg"
      />
      <StatBlock
        :value="String(view.gapCells)"
        caption="Reporting periods still on estimate"
        size="lg"
        :warn="view.gapCells > 0"
      />
      <StatBlock
        :value="fmt(view.gapVolume)"
        unit="MWh"
        caption="Volume standing on those estimates"
        size="lg"
      />
    </div>
    <div class="cg-subsection">
      <h3 class="cg-card-title mb-3">
        Data completeness by consumption location
      </h3>
      <CompletenessGrid :rows="view.rows" :legend="view.legend" />
    </div>

    <div class="cg-subsection">
      <h3 class="cg-card-title mb-3">Domain totals by reporting period</h3>
      <DomainTotalsChart
        :rows="view.monthRows"
        :legend="view.monthLegend"
        :total="domain.consumption.fy"
        @open-month="$emit('open-month', $event)"
      />
    </div>

    <div class="cg-subsection">
      <h3 class="cg-card-title mb-3">Consumption locations</h3>
      <div class="table-responsive">
        <table class="table table-sm mb-0 align-middle small">
          <thead>
            <tr>
              <th class="cg-micro">Consumption location</th>
              <th class="cg-micro">Legal entity</th>
              <th class="cg-micro">Electricity supplier</th>
              <th class="cg-micro">Reported by</th>
              <th class="cg-micro text-end">Actual reported</th>
              <th class="cg-micro text-end">Estimated</th>
              <th class="cg-micro text-end">2026 total</th>
            </tr>
          </thead>
          <tbody class="cg-tabular">
            <tr v-for="mp in view.meterTable" :key="mp.id">
              <td class="fw-medium">{{ mp.name }}</td>
              <td class="text-body-secondary">{{ mp.entity }}</td>
              <td>{{ mp.supplier }}</td>
              <td><ToneBadge :tone="mp.entryTone" :label="mp.entry" /></td>
              <td class="text-end">{{ fmt(mp.actual) }}</td>
              <td class="text-end">{{ fmt(mp.estimated) }}</td>
              <td class="text-end fw-semibold">{{ fmt(mp.fy) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import type {
  ConsumptionView,
  DerivedDomain,
} from "@/compliance_groups/fixture/derive";
import { fmt } from "@/compliance_groups/fixture/format";
import CompletenessGrid from "./CompletenessGrid.vue";
import DomainTotalsChart from "./DomainTotalsChart.vue";
import SectionCard from "./SectionCard.vue";
import StatBlock from "./StatBlock.vue";
import ToneBadge from "./ToneBadge.vue";

defineProps<{ domain: DerivedDomain; view: ConsumptionView }>();
defineEmits<{ "open-month": [monthIndex: number]; report: [] }>();
</script>

<style scoped lang="scss">
.cg-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}
// Each part of the card gets a hairline and room on both sides of it, so the sections read as
// neighbours rather than a stack.
.cg-subsection {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--phoenix-card-border-color);
}
</style>
